/**
 * SafeEmailHtml — renders inbound email HTML the way real webmail clients do:
 * inside an isolated, sandboxed iframe.
 *
 * Inbound email is the highest-risk HTML surface in the product: it is authored
 * by external parties (solicitors, courts, unknown senders) and frequently ships
 * its own <style> blocks, tables and layout CSS (GitLab, court portals, chambers
 * newsletters). Rendering it in a plain <div> either strips that CSS (losing all
 * formatting — the email collapses to plain text) or lets it bleed into the app.
 *
 * The iframe solves both:
 *
 *  1. Isolation — the email's CSS lives in its own document. Its <style> blocks
 *     render exactly as the sender intended and cannot touch the surrounding app.
 *  2. Sanitisation — DOMPurify strips <script>, event handlers, <iframe>, <form>
 *     and javascript: URLs before the HTML ever reaches the frame, and the frame
 *     runs WITHOUT `allow-scripts`, so even a missed script cannot execute.
 *  3. Remote-image blocking — by default remote images (incl. 1×1 tracking
 *     pixels) are neutralised so opening an email does not leak the barrister's
 *     IP / read-status to the sender. A "Show images" control loads them on
 *     demand, exactly as Gmail and Outlook do.
 *  4. Links open safely — every link is forced to a new tab with no window.opener
 *     access (no reverse-tabnabbing) via `allow-popups-to-escape-sandbox`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';

// 1×1 transparent GIF — used to neutralise a blocked remote image so it leaves
// NO broken-image icon.
const TRANSPARENT_PX =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

function sanitiseEmail(html: string, showImages: boolean): { clean: string; hadRemoteImages: boolean } {
  let hadRemoteImages = false;

  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    // Detect and optionally neutralise remote image/background sources
    if (
      (data.attrName === 'src' || data.attrName === 'srcset' || data.attrName === 'background') &&
      /^https?:/i.test(data.attrValue)
    ) {
      hadRemoteImages = true;
      if (!showImages) {
        if (data.attrName === 'src') data.attrValue = TRANSPARENT_PX;
        else data.keepAttr = false;
      }
    }
  });

  // Force every link to open in a new tab WITHOUT access to window.opener,
  // preventing reverse-tabnabbing from a malicious email link.
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName === 'A' && node.hasAttribute('href')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer nofollow');
    }
  });

  // WHOLE_DOCUMENT lets the email keep its own <style> blocks (safe now — the
  // whole thing renders in an isolated iframe, so CSS can't reach the app).
  const clean = DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'meta'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onmouseenter'],
    ADD_ATTR: ['target'],
  });

  DOMPurify.removeAllHooks();
  return { clean, hadRemoteImages };
}

// Neutral typography, images that never overflow, links styled like a mail
// client so plain-text-ish emails still read as email. The email's own <style>
// comes AFTER and wins where it sets anything explicitly.
function baseStyle(fontSize: number): string {
  return `<style>
    html,body{margin:0;padding:0;background:#ffffff;color:#1a1a1a;}
    body{font-family:'IBM Plex Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;
         font-size:${fontSize}px;line-height:1.6;word-break:break-word;overflow-wrap:anywhere;padding:2px;}
    img{max-width:100%;height:auto;}
    table{max-width:100%;}
    a{color:#2563eb;}
    ::-webkit-scrollbar{height:8px;width:8px;}
    ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:4px;}
  </style>`;
}

function buildSrcDoc(cleanHtml: string, fontSize: number): string {
  const base = baseStyle(fontSize);
  // If DOMPurify returned a full document, inject <base> + our reset into <head>;
  // otherwise wrap the fragment in a minimal document.
  if (/<html[\s>]/i.test(cleanHtml)) {
    if (/<head[\s>]/i.test(cleanHtml)) {
      return cleanHtml.replace(/<head([^>]*)>/i, `<head$1><base target="_blank">${base}`);
    }
    return cleanHtml.replace(/<html([^>]*)>/i, `<html$1><head><base target="_blank">${base}</head>`);
  }
  return `<!doctype html><html><head><base target="_blank">${base}</head><body>${cleanHtml}</body></html>`;
}

// A body FRAGMENT (main or quoted portion) rebuilt into a full document that
// still carries the email's own <head> styles, so a split half looks identical
// to the whole. `headHtml` is the original email head (its <style> blocks).
function buildFragmentDoc(headHtml: string, bodyHtml: string, fontSize: number): string {
  return `<!doctype html><html><head><base target="_blank">${baseStyle(fontSize)}${headHtml}</head><body>${bodyHtml}</body></html>`;
}

// Where quoted / trimmed history begins. These containers wrap the previous
// message(s) in a reply across the major clients.
const QUOTE_SELECTORS = [
  '.gmail_quote',
  'blockquote[type="cite"]',
  '#divRplyFwdMsg',            // Outlook
  '#appendonsend',            // Outlook
  '#mail-editor-reference-message-container',
  'div.moz-cite-prefix',      // Thunderbird
].join(',');

interface SplitBody { headHtml: string; mainHtml: string; quotedHtml: string }

// Split a sanitised email into the current message and the quoted history.
// Returns null when there's no quote, or nothing meaningful precedes it (so we
// never hide the whole message). Best-effort — any parsing error → no split.
function splitQuotedHtml(clean: string): SplitBody | null {
  try {
    const doc = new DOMParser().parseFromString(
      /<html[\s>]/i.test(clean) ? clean : `<html><body>${clean}</body></html>`,
      'text/html',
    );
    const body = doc.body;
    if (!body) return null;
    const boundary = body.querySelector(QUOTE_SELECTORS);
    if (!boundary) return null;

    // Walk up to the direct child of <body> that contains the quote boundary.
    let topLevel: Node = boundary;
    while (topLevel.parentElement && topLevel.parentElement !== body) topLevel = topLevel.parentElement;

    const nodes = Array.from(body.childNodes);
    const idx = nodes.indexOf(topLevel as ChildNode);
    if (idx <= 0) return null; // quote is the first thing → nothing to trim

    const mainDiv = doc.createElement('div');
    const quotedDiv = doc.createElement('div');
    nodes.forEach((node, i) => (i < idx ? mainDiv : quotedDiv).appendChild(node.cloneNode(true)));

    // Only fold when the main part actually has content to read.
    const hasText = (mainDiv.textContent ?? '').trim().length > 0;
    const hasMedia = !!mainDiv.querySelector('img,table');
    if (!hasText && !hasMedia) return null;

    return { headHtml: doc.head?.innerHTML ?? '', mainHtml: mainDiv.innerHTML, quotedHtml: quotedDiv.innerHTML };
  } catch {
    return null;
  }
}

// One isolated, auto-sizing email frame. Same-origin srcDoc but NO allow-scripts
// (nothing in the email can execute); height is measured from the parent and
// re-measured as images load.
function EmailFrame({ srcDoc }: { srcDoc: string }) {
  const [height, setHeight] = useState(80);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const measure = () => {
      const doc = frame.contentDocument;
      if (!doc?.body) return;
      const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
      if (h > 0) setHeight(h + 4);
    };
    const onLoad = () => {
      measure();
      frame.contentDocument?.querySelectorAll('img').forEach((img) => {
        if (!img.complete) img.addEventListener('load', measure, { once: true });
      });
      setTimeout(measure, 250);
    };
    frame.addEventListener('load', onLoad);
    if (frame.contentDocument?.readyState === 'complete') onLoad();
    return () => frame.removeEventListener('load', onLoad);
  }, [srcDoc]);

  return (
    <iframe
      ref={frameRef}
      title="Email content"
      srcDoc={srcDoc}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{
        width: '100%', height, border: 'none', display: 'block',
        background: '#ffffff', borderRadius: 6, colorScheme: 'light',
      }}
    />
  );
}

export function SafeEmailHtml({ html, fontSize = 14 }: { html: string; fontSize?: number }) {
  const [showImages, setShowImages] = useState(false);
  const [showQuoted, setShowQuoted] = useState(false);

  const { clean, hadRemoteImages } = useMemo(() => sanitiseEmail(html, showImages), [html, showImages]);
  const split = useMemo(() => splitQuotedHtml(clean), [clean]);

  const mainSrcDoc = useMemo(
    () => (split ? buildFragmentDoc(split.headHtml, split.mainHtml, fontSize) : buildSrcDoc(clean, fontSize)),
    [split, clean, fontSize],
  );
  const quotedSrcDoc = useMemo(
    () => (split ? buildFragmentDoc(split.headHtml, split.quotedHtml, fontSize) : ''),
    [split, fontSize],
  );

  return (
    <div>
      {hadRemoteImages && !showImages && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, padding: '7px 12px', marginBottom: 12,
          background: 'var(--surface-hover)', border: '1px solid var(--border-default)',
          borderRadius: 6,
        }}>
          <span style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, color: 'var(--text-muted)' }}>
            Images are blocked to protect your privacy.
          </span>
          <button
            onClick={() => setShowImages(true)}
            style={{
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, fontWeight: 600,
              color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}
          >
            Show images
          </button>
        </div>
      )}

      <EmailFrame srcDoc={mainSrcDoc} />

      {split && (
        <div style={{ marginTop: 6 }}>
          <button
            onClick={() => setShowQuoted((v) => !v)}
            title={showQuoted ? 'Hide trimmed content' : 'Show trimmed content'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '1px 8px', lineHeight: 1.2,
              background: 'var(--surface-hover)', border: '1px solid var(--border-default)',
              borderRadius: 10, cursor: 'pointer',
              fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12, fontWeight: 700,
              color: 'var(--text-muted)', letterSpacing: 1,
            }}
          >
            •••
          </button>
          {!showQuoted && (
            <span style={{
              marginLeft: 8, fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 11.5, color: 'var(--text-subtle)',
            }}>
              Trimmed content
            </span>
          )}
          {showQuoted && (
            <div style={{ marginTop: 6, borderLeft: '2px solid var(--border-default)', paddingLeft: 10 }}>
              <EmailFrame srcDoc={quotedSrcDoc} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
